/**
 * Linked Table Configuration Types
 *
 * Request body types and configuration objects for linked tables.
 * These types are used for UI state management and API request payloads.
 *
 * Note: Response types are inferred from the Hono RPC AppType.
 * Use the RPC client for type-safe API calls instead of explicit response types.
 */

import type { CalculatedColumnDataType } from '../constants/common';

/**
 * Table reference in SQL-native linked tables
 */
export interface LinkedTableSourceApi {
  id: string;
  type: 'table' | 'linked_table';
  target_id: string;
  alias: string;
}

/**
 * Join condition for SQL-native joins
 *
 * Supports two modes:
 * - Field mode: left_field + right_field (field UUIDs)
 * - Formula mode: left_formula + right_formula (formula expressions like UPPER([field]))
 */
export interface LinkedTableJoinConditionApi {
  left_field?: string;
  operator: '=' | '!=' | '>' | '<' | '>=' | '<=';
  right_field?: string;
  left_formula?: string;
  right_formula?: string;
}

/**
 * Linked Table Join structure (SQL-native)
 */
export interface LinkedTableJoinApi {
  id: string;
  left_table: string;
  right_table: string;
  join_type: 'LEFT' | 'INNER' | 'RIGHT' | 'FULL';
  conditions: LinkedTableJoinConditionApi[];
}

/**
 * Linked Table Filter (API format with snake_case)
 */
export interface LinkedTableFilterApi {
  id: string;
  table: string;
  field_id: string;
  field_name?: string;
  operator:
    | 'equals'
    | 'not_equals'
    | 'contains'
    | 'not_contains'
    | 'greater_than'
    | 'less_than'
    | 'greater_than_or_equal'
    | 'less_than_or_equal'
    | 'in'
    | 'not_in'
    | 'is_null'
    | 'is_not_null';
  value: string | number | boolean | string[] | null;
  logical_operator?: 'AND' | 'OR';
  /** When set, filter value is resolved from the named variable instead of the static value */
  variable_name?: string;
}

/**
 * Filter operator type extracted from LinkedTableFilterApi.
 * Use this for strongly typing filter operators in frontend/backend code.
 */
export type LinkedTableFilterOperator = LinkedTableFilterApi['operator'];

/**
 * Join type extracted from LinkedTableJoinApi.
 * Use this for strongly typing join types in frontend/backend code.
 */
export type LinkedTableJoinType = LinkedTableJoinApi['join_type'];

/**
 * Linked Table Calculated Column (API format)
 */
export interface LinkedTableCalculatedColumnApi {
  id: string;
  name: string;
  formula_id: string;
  formula_value: string;
  description?: string;
  dependencies?: string[];
  data_type?: CalculatedColumnDataType;
}

/**
 * Linked Table Value Configuration
 * Defines aggregation functions to apply to fields in pivot table values
 */
export interface LinkedTableValueApi {
  field_id: string;
  function: 'sum' | 'avg' | 'count' | 'min' | 'max';
}

/**
 * Linked Table Row Group Sort Configuration
 * Defines sort order for row groups (ORDER BY clause)
 */
export interface LinkedTableRowGroupSortApi {
  field_id: string;
  direction: 'asc' | 'desc';
}

/**
 * Maximum length for variable names
 */
export const VARIABLE_NAME_MAX_LENGTH = 50;

/**
 * Linked Table Variable
 * Named parameter with a type and default value, referenceable in filters and formulas.
 */
export interface LinkedTableVariableApi {
  id: string;
  /** Variable name — valid identifier (letters, numbers, underscores) */
  name: string;
  /** Data type for SQL coercion */
  type: 'text' | 'number' | 'date';
  /** Default value (string representation, used for preview and fallback) */
  default_value: string;
}

// ============================================================================
// Request Types
// ============================================================================

/**
 * Linked Table Create Request
 */
export interface LinkedTableCreateRequest {
  name: string;
  description?: string | null;
  folder_id?: string | null;
  tables: LinkedTableSourceApi[];
  joins?: LinkedTableJoinApi[] | null;
  filters?: LinkedTableFilterApi[] | null;
  calculated_columns?: LinkedTableCalculatedColumnApi[] | null;
  row_groups?: string[] | null;
  row_group_sorts?: LinkedTableRowGroupSortApi[] | null;
  values?: LinkedTableValueApi[] | null;
  variables?: LinkedTableVariableApi[] | null;
}

/**
 * Linked Table Update Request
 */
export interface LinkedTableUpdateRequest {
  name?: string;
  description?: string | null;
  status?: 'active' | 'disabled';
  tables?: LinkedTableSourceApi[];
  joins?: LinkedTableJoinApi[] | null;
  filters?: LinkedTableFilterApi[] | null;
  calculated_columns?: LinkedTableCalculatedColumnApi[] | null;
  row_groups?: string[] | null;
  row_group_sorts?: LinkedTableRowGroupSortApi[] | null;
  values?: LinkedTableValueApi[] | null;
  variables?: LinkedTableVariableApi[] | null;
}

/**
 * Linked Table Preview Request
 */
export interface LinkedTablePreviewRequest {
  filters?: LinkedTableFilterApi[] | null;
  calculated_columns?: LinkedTableCalculatedColumnApi[] | null;
  tables?: LinkedTableSourceApi[] | null;
  joins?: LinkedTableJoinApi[] | null;
  row_groups?: string[] | null;
  row_group_sorts?: LinkedTableRowGroupSortApi[] | null;
  values?: LinkedTableValueApi[] | null;
  variables?: LinkedTableVariableApi[] | null;
  // Pagination (can be sent in body or query params)
  limit?: number;
  offset?: number;
  // Version selections for each table alias
  versionSelections?: Record<string, string>;
}

/**
 * Linked Table Preview Query Parameters
 */
export interface LinkedTablePreviewParams {
  table_versions?: Record<string, string>;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  filters?: unknown[];
  search?: string;
}

// ============================================================================
// Join CRUD Request Types
// ============================================================================

/**
 * Request body for creating a new join
 * The source (tables entry) is created implicitly with the join
 */
export interface LinkedTableJoinCreateRequest {
  /** Target table or linked table to join */
  target_id: string;
  /** Type of target: regular table or another linked table */
  target_type: 'table' | 'linked_table';
  /** Alias for the joined table (must be unique, valid SQL identifier) */
  alias: string;
  /** Join type: LEFT (default), INNER, RIGHT, FULL */
  join_type?: 'LEFT' | 'INNER' | 'RIGHT' | 'FULL';
  /** Join conditions mapping source fields to target fields */
  conditions: LinkedTableJoinConditionApi[];
  /** Description of the relationship (optional, for UI) */
  description?: string;
}

/**
 * Request body for updating an existing join
 */
export interface LinkedTableJoinUpdateRequest {
  /** New alias (optional) */
  alias?: string;
  /** New join type (optional) */
  join_type?: 'LEFT' | 'INNER' | 'RIGHT' | 'FULL';
  /** New join conditions (optional, replaces all existing) */
  conditions?: LinkedTableJoinConditionApi[];
  /** Updated description (optional) */
  description?: string;
}

/**
 * Response for join operations
 */
export interface LinkedTableJoinResponse {
  /** The join record */
  join: LinkedTableJoinApi;
  /** The corresponding source entry */
  source: LinkedTableSourceApi;
  /** Validation warnings (if any) */
  warnings?: string[];
}

/**
 * Response for listing all joins
 */
export interface LinkedTableJoinsListResponse {
  /** All joins in the linked table */
  joins: LinkedTableJoinApi[];
  /** All source tables (for reference) */
  sources: LinkedTableSourceApi[];
}

// ============================================================================
// Preview Response Types
// ============================================================================

/**
 * Field metadata returned by linked table preview
 * Contains information about each field available in the query
 */
export interface LinkedTableFieldMetadata {
  /** Field UUID (unique identifier) */
  id: string;
  /** Field name (used as key in result records) */
  name: string;
  /** Parent table UUID */
  table_id: string;
  /** Field data type */
  data_type: string;
  /** SQL table alias for this field's source */
  table_alias: string;
}

/**
 * Normalized config changes returned when config cleanup occurs
 */
export interface LinkedTableConfigChanges {
  filters_removed: number;
  calculated_columns_removed: number;
  row_groups_cleaned: number;
  values_cleaned: number;
}

/**
 * Normalized configuration returned from preview when cleanup occurs
 */
export interface LinkedTableNormalizedConfig {
  filters: LinkedTableFilterApi[];
  calculated_columns: LinkedTableCalculatedColumnApi[];
  row_groups: string[];
  values: LinkedTableValueApi[];
  _changes: LinkedTableConfigChanges;
}

/**
 * Response from linked table preview endpoint
 * Includes both result data and field metadata for the column picker
 */
export interface LinkedTablePreviewResponse {
  /** Result data rows */
  data: Record<string, unknown>[];
  /** Pagination information */
  pagination: {
    total: number;
    limit: number;
    offset: number;
    remaining?: number;
  };
  /** Field metadata for fields in the result (ordered by row_groups, then values) */
  fields: LinkedTableFieldMetadata[];
  /** Field metadata for ALL available fields from all tables (for column picker) */
  available_fields: LinkedTableFieldMetadata[];
  /** Calculated columns configuration */
  calculated_columns?: LinkedTableCalculatedColumnApi[];
  /** Normalized configuration (only present if cleanup occurred) */
  normalized_config?: LinkedTableNormalizedConfig;
  /** Query optimization metadata (only present if optimizations were applied) */
  optimization_metadata?: LinkedTableOptimizationMetadata;
}

// ============================================================================
// Linked Table API Response Types
// ============================================================================

/**
 * Linked Table Status
 */
export type LinkedTableStatus = 'active' | 'disabled';

/**
 * Linked Table API Response
 * Full linked table object returned from API endpoints
 */
export interface LinkedTableApiResponse {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  status: LinkedTableStatus;
  folder_id: string | null;
  is_favorite: boolean;
  deleted_at: string | null;
  tables: LinkedTableSourceApi[];
  joins: LinkedTableJoinApi[] | null;
  filters: LinkedTableFilterApi[] | null;
  calculated_columns: LinkedTableCalculatedColumnApi[] | null;
  row_groups: string[] | null;
  row_group_sorts: LinkedTableRowGroupSortApi[] | null;
  values: LinkedTableValueApi[] | null;
  variables: LinkedTableVariableApi[] | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** Field metadata for all available fields from source tables (for config UI) */
  available_fields?: LinkedTableFieldMetadata[];
}

// ============================================================================
// Query Optimization Types
// ============================================================================

/**
 * Cardinality error returned when query would produce too many intermediate rows.
 * This is a client-fixable error (HTTP 400) that should be displayed inline in the UI.
 */
export interface LinkedTableCardinalityError {
  /** Human-readable error message */
  error: string;
  /** Error code for programmatic handling */
  code: 'CARDINALITY_EXCEEDED';
  /** Detailed information about the cardinality issue */
  details: {
    /** Estimated number of intermediate rows the query would produce */
    estimatedRows: number;
    /** Threshold that was exceeded */
    threshold: number;
    /** Row counts per table alias */
    tableRowCounts: Record<string, number>;
    /** Actionable suggestions to fix the issue */
    suggestions: string[];
  };
}

/**
 * Metadata about query optimizations that were applied.
 * Returned in successful responses when optimizations occurred.
 */
export interface LinkedTableOptimizationMetadata {
  /** Number of unused joins that were pruned */
  joinsPruned: number;
  /** Aliases of tables whose joins were removed */
  prunedJoinAliases: string[];
  /** Warnings about joins that weren't removed (INNER/RIGHT/FULL) */
  joinWarnings: string[];
  /** Names of pre-aggregation CTEs that were generated */
  preAggregationCTEs: string[];
  /** Table aliases that use scalar subqueries instead of JOINs */
  scalarSubqueryTables: string[];
}
